import { useState, useEffect, useCallback } from 'react';
import { Upload, Trash2, Image } from 'lucide-react';
import Select from '@/components/ui/Select';
import Input from '@/components/ui/Input';
import { photosApi } from '@/api';

const CATEGORIES = [
  { value: 'exterieur', label: 'Extérieur' },
  { value: 'interieur', label: 'Intérieur' },
  { value: 'alentours', label: 'Alentours' },
  { value: 'facade', label: 'Façade' },
  { value: 'plan', label: 'Plan' },
  { value: 'cadastre', label: 'Cadastre' },
  { value: 'dpe', label: 'DPE' },
  { value: 'environnement', label: 'Environnement' },
];

export default function SectionPhotos({ expertiseId }) {
  const [photos, setPhotos] = useState([]);
  const [uploading, setUploading] = useState(false);

  const loadPhotos = useCallback(async () => {
    if (!expertiseId) return;
    try {
      const data = await photosApi.list(expertiseId);
      setPhotos(data || []);
    } catch {
      // silent
    }
  }, [expertiseId]);

  useEffect(() => {
    loadPhotos();
  }, [loadPhotos]);

  const handleUpload = async (files) => {
    if (!files?.length || !expertiseId) return;
    setUploading(true);
    try {
      for (const file of files) {
        await photosApi.upload(expertiseId, file, 'exterieur', '');
      }
      await loadPhotos();
    } catch {
      // silent
    } finally {
      setUploading(false);
    }
  };

  const handleDelete = async (photoId) => {
    await photosApi.delete(photoId);
    await loadPhotos();
  };

  const handleUpdateCategorie = async (photoId, categorie) => {
    await photosApi.update(photoId, { categorie });
    await loadPhotos();
  };

  const handleUpdateLegende = async (photoId, legende) => {
    await photosApi.update(photoId, { legende });
  };

  return (
    <div className="space-y-6">
      <h3 className="text-lg font-semibold text-slate-900">Photos</h3>

      {/* Upload zone */}
      <div
        className="border-2 border-dashed border-slate-300 rounded-lg p-8 text-center hover:border-carex-400 transition-colors cursor-pointer"
        onDragOver={(e) => {
          e.preventDefault();
          e.stopPropagation();
        }}
        onDrop={(e) => {
          e.preventDefault();
          e.stopPropagation();
          handleUpload(e.dataTransfer.files);
        }}
        onClick={() => document.getElementById('photo-upload-input')?.click()}
      >
        <Upload className="w-8 h-8 text-slate-400 mx-auto mb-2" />
        <p className="text-sm text-slate-500">
          {uploading
            ? 'Téléversement en cours...'
            : 'Glisser-déposer des photos ou cliquer pour sélectionner'}
        </p>
        <input
          id="photo-upload-input"
          type="file"
          accept="image/*"
          multiple
          className="hidden"
          onChange={(e) => handleUpload(e.target.files)}
        />
      </div>

      {/* Photo grid */}
      {photos.length > 0 ? (
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
          {photos.map((photo) => (
            <div
              key={photo.id}
              className="border border-slate-200 rounded-lg overflow-hidden"
            >
              <div className="aspect-square bg-slate-100 flex items-center justify-center">
                {photo.thumbnail_url ? (
                  <img
                    src={photo.thumbnail_url}
                    alt={photo.legende || ''}
                    className="w-full h-full object-cover"
                  />
                ) : (
                  <Image className="w-8 h-8 text-slate-300" />
                )}
              </div>
              <div className="p-2 space-y-2">
                <Select
                  options={CATEGORIES}
                  value={photo.categorie || 'exterieur'}
                  onChange={(e) =>
                    handleUpdateCategorie(photo.id, e.target.value)
                  }
                />
                <Input
                  type="text"
                  placeholder="Légende"
                  value={photo.legende || ''}
                  onChange={(e) =>
                    handleUpdateLegende(photo.id, e.target.value)
                  }
                />
                <button
                  onClick={() => handleDelete(photo.id)}
                  className="text-xs text-red-500 hover:text-red-700 flex items-center gap-1"
                >
                  <Trash2 className="w-3 h-3" /> Supprimer
                </button>
              </div>
            </div>
          ))}
        </div>
      ) : (
        <p className="text-sm text-slate-400 italic">Aucune photo.</p>
      )}
    </div>
  );
}
